import React, { useState, useEffect } from "react";
import Auth from "./Auth";
import AuthFooter from "./AuthFooter";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiRoutes } from "constants/apiRoutes";
import { client } from "constants/api";
import { inviteView, withInvite } from "utilities/inviteAuth";

function AuthSection(props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteParam = searchParams.get("invite") || null;

  const [inviteContext, setInviteContext] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  // SOUND-752. Every decision about the invite comes from here — validating,
  // the spinner, hiding the form, dropping a dead token, and which links carry
  // it. They used to be decided separately, and each route off this page lost
  // the invitation in its own way.
  const invite = inviteView({
    type: props.type,
    token: inviteParam,
    context: inviteContext,
    error: inviteError,
  });

  // Validate on signup AND signin (new invitations only, not legacy data links).
  useEffect(() => {
    if (!invite.validate) return;

    client
      .get(apiRoutes.validateInvitation(inviteParam))
      .then((res) => setInviteContext(res.data))
      .catch((err) => {
        setInviteError(
          err.response?.data?.error || "This invitation is no longer valid."
        );
      });
  }, [invite.validate, inviteParam]);

  // A dead token must never reach POST /users from sign-in: the API fails
  // closed on it and deletes an unprovisioned Firebase account. auth.js reads
  // the token off the URL, so removing it here is what stops it being sent.
  useEffect(() => {
    if (!invite.dropToken) return;
    const next = new URLSearchParams(searchParams);
    next.delete("invite");
    setSearchParams(next, { replace: true });
  }, [invite.dropToken, searchParams, setSearchParams]);

  // Dead end 1: the invited address already has an account. Signing in with it
  // accepts the invitation — POST /users does not need a fresh account.
  const handleExistingAccount = () =>
    navigate(withInvite("/auth/signin", invite.linkToken));

  const optionsByType = {
    signup: {
      title: inviteContext
        ? `Join ${inviteContext.companyName}`
        : "Let's get you an account",
      buttonAction: "Create Account",
      showFooter: true,
      signinText: "Already have an account?",
      signinAction: "Sign in",
      signinPath: withInvite("/auth/signin", invite.linkToken),
      showAgreement: true,
    },
    signin: {
      title: inviteContext
        ? `Sign in to join ${inviteContext.companyName}`
        : "Welcome back!",
      buttonAction: "Sign in",
      showFooter: true,
      signupAction: "Create an account",
      signupPath: withInvite("/auth/signup", invite.linkToken),
      forgotPassAction: "Forgot Password?",
      forgotPassPath: "/auth/forgotpass",
    },
    forgotpass: {
      title: "Forgot your password?",
      buttonAction: "Reset password",
      showFooter: true,
      signinText: "Remember it after all?",
      signinAction: "Sign in",
      signinPath: "/auth/signin",
    },
    changepass: {
      title: "Choose a new password",
      buttonAction: "Change password",
    },
  };

  const type = optionsByType[props.type] ? props.type : "signup";
  const options = optionsByType[type];

  return (
    <section className="dark flex items-center h-screen bg-background font-jakarta text-foreground px-4">
      <div className="container mx-auto max-w-sm text-center p-[30px] rounded-[30px] shadow-lg border border-border">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
            <Link to="/">
              Sound<span className="text-brand">.</span>
            </Link>
          </h2>
        </div>
        <h1 className="mt-5 mb-5 text-xl font-medium tracking-tight text-foreground">
          {options.title}
        </h1>

        {inviteError && ["signup", "signin"].includes(type) && (
          <div className="mb-4 p-3 rounded-lg bg-red-900/20 border border-red-800 text-red-400 text-sm">
            {inviteError}
          </div>
        )}

        {inviteContext && ["signup", "signin"].includes(type) && (
          <p className="mb-4 text-sm text-muted-foreground">
            You've been invited as <span className="font-medium text-foreground capitalize">{inviteContext.role}</span>
          </p>
        )}

        {invite.state === "loading" ? (
          <div className="py-8 text-sm text-muted-foreground">Loading invitation...</div>
        ) : invite.showForm ? (
          <Auth
            type={type}
            buttonAction={options.buttonAction}
            providers={props.providers}
            afterAuthPath={props.afterAuthPath}
            inviteEmail={inviteContext?.email || null}
            onExistingAccount={handleExistingAccount}
            key={inviteContext?.email || type}
          />
        ) : (
          // Dead end 4. Under a dead invite the signup form is the SELF-SERVE
          // form, so it is not offered. Signing in is the answer either way:
          // an accepted invite already made the account.
          <div className="space-y-4 text-sm text-muted-foreground">
            <p>
              If you've already accepted this invitation, sign in with the
              account you created. Otherwise, ask the person who invited you to
              send a new one.
            </p>
            <Link to="/auth/signin" className="btn-primary w-full inline-flex justify-center">
              Sign in
            </Link>
          </div>
        )}
        {options.showFooter && invite.showForm && <AuthFooter type={type} {...options} />}
      </div>
    </section>
  );
}

export default AuthSection;
